import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import styled from 'styled-components';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { sanitizeRichText } from 'services/SanitizeService';
import { Colors } from 'context';
import { PATHS } from 'router/config';
import { ProfileCompletenessBar } from 'molecules';

export interface ICareerExerciseResult {
  outro: string;
  writtenTargets: string[];
  skippedTargets: string[];
  profileCompleteness: number;
}

/**
 * Карточка итога после сдачи анкеты (ТЗ 0039).
 *
 * Показывает НЕ только «спасибо», а что именно попало в резюме и что
 * пропущено. Пропущенное — не ошибка: это места, которые студент уже поправил
 * руками, и анкета их сознательно не трогает. Без такого списка правило
 * конфликта выглядело бы как потеря данных.
 */
export const CareerExerciseResult = memo(
  ({ outro, writtenTargets, skippedTargets, profileCompleteness }: ICareerExerciseResult) => {
    const { t } = useTranslation();
    // 🔴 Точка в ключе — разделитель уровней у i18next, а привязки как раз
    // точку и содержат (`cv.about`). Пока это работало лишь потому, что
    // i18next по умолчанию доискивает плоский ключ (`ignoreJSONStructure`):
    // одна настройка в конфиге — и студент увидел бы в карточке итога сырое
    // `cv.about` вместо «О себе», без единой ошибки в консоли.
    const label = (target: string) =>
      t(`careerCenter.exercise.target.${target.replace(/\./g, '_')}`, {
        defaultValue: target,
      });

    return (
      <Wrapper>
        <Title>{t('careerCenter.exercise.doneTitle')}</Title>

        {outro ? <Outro dangerouslySetInnerHTML={{ __html: sanitizeRichText(outro) }} /> : null}

        {writtenTargets.length ? (
          <Block>
            <BlockTitle>{t('careerCenter.exercise.written')}</BlockTitle>
            <Chips>
              {writtenTargets.map((target) => (
                <Chip key={target}>{label(target)}</Chip>
              ))}
            </Chips>
          </Block>
        ) : null}

        {skippedTargets.length ? (
          <Block>
            <BlockTitle>{t('careerCenter.exercise.skipped')}</BlockTitle>
            <Chips>
              {skippedTargets.map((target) => (
                <MutedChip key={target}>{label(target)}</MutedChip>
              ))}
            </Chips>
            <Note>{t('careerCenter.exercise.skippedNote')}</Note>
          </Block>
        ) : null}

        <Block>
          <ProfileCompletenessBar
            percent={profileCompleteness}
            title={t('careerCenter.exercise.completeness')}
          />
        </Block>

        <Actions>
          <ActionLink to={PATHS.careerProfile}>{t('careerCenter.exercise.openResume')}</ActionLink>
        </Actions>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  margin-top: 28px;
  padding: 20px;
  border-radius: 12px;
  background-color: ${Colors.bg_gray};
`;

const Title = styled.h3`
  margin: 0 0 12px 0;
  ${fontService.text({ type: TextType.medium, weight: FontWeight.bold })}
`;

const Outro = styled.div`
  margin-bottom: 16px;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const Block = styled.div`
  margin-bottom: 16px;
`;

const BlockTitle = styled.p`
  margin: 0 0 8px 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.span`
  padding: 4px 10px;
  border-radius: 8px;
  background-color: ${Colors.main};
  color: ${Colors.btn_text};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const MutedChip = styled(Chip)`
  background-color: ${Colors.additional_gray};
  color: ${Colors.gray_dark};
`;

const Note = styled.p`
  margin: 8px 0 0 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.regular })}
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const ActionLink = styled(Link)`
  padding: 10px 18px;
  border-radius: 10px;
  background-color: ${Colors.main};
  color: ${Colors.btn_text};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    opacity: 0.9;
    color: ${Colors.btn_text};
  }
`;
